import { compareAsc, format, parse } from 'date-fns';

import type { DisplayStore } from '@libs/common-types';

import type { StoreDisplayTimeFormat } from '../types/store-time';

const defaultOpenTime = '09:00:00';
const defaultCloseTime = '21:00:00';

const getStoreHours = (store: DisplayStore) => store.StandardStoreHours ?? store.CurrentStoreHours ?? [];

export const hasStoreHoursData = (store: DisplayStore): boolean => getStoreHours(store).length > 0;

const getTodaysHours = (store: DisplayStore, day: number) =>
    getStoreHours(store).find(d => d.DayOfWeek === day) ?? null;

const isStoreClosedForDay = (store: DisplayStore, day: number) => {
    const today = getTodaysHours(store, day);
    return today?.IsClosed ?? false;
};

export const getClosingTime = (store: DisplayStore, currentDate: Date, day: number): Date => {
    const today = getTodaysHours(store, day);

    if (today?.CloseTime) {
        return parse(today.CloseTime, 'HH:mm:ss', currentDate);
    }

    return parse(defaultCloseTime, 'HH:mm:ss', currentDate);
};

export const getOpeningTime = (store: DisplayStore, currentDate: Date, day: number): Date => {
    const today = getTodaysHours(store, day);

    if (today?.OpenTime) {
        return parse(today.OpenTime, 'HH:mm:ss', currentDate);
    }

    return parse(defaultOpenTime, 'HH:mm:ss', currentDate);
};

export const getOpeningTimeToday = (store: DisplayStore, currentDate: Date) =>
    getOpeningTime(store, currentDate, currentDate.getDay());

export const getClosingTimeToday = (store: DisplayStore, currentDate: Date) =>
    getClosingTime(store, currentDate, currentDate.getDay());

export const getNextOpeningTime = (
    store: DisplayStore,
    currentDate: Date
): { nextOpeningTime: Date | null; openingDay: string | null } => {
    const currentDay = currentDate.getDay();
    const storeHours = getStoreHours(store);

    // Check today first - if store opens today but it's before opening time, return "today"
    const todayHours = storeHours.find(d => d.DayOfWeek === currentDay);

    if (todayHours && !todayHours.IsClosed && todayHours.OpenTime) {
        const todayOpeningTime = getOpeningTime(store, currentDate, currentDay);
        if (compareAsc(currentDate, todayOpeningTime) === -1) {
            // If current time is before opening time, return "today"
            return {
                nextOpeningTime: todayOpeningTime,
                openingDay: 'today',
            };
        }
    }

    // Check the next 7 days starting from tomorrow
    for (let i = 1; i < 7; i++) {
        const dayToCheck = (currentDay + i) % 7;
        const dayHours = storeHours.find(d => d.DayOfWeek === dayToCheck);

        if (dayHours && !dayHours.IsClosed && dayHours.OpenTime) {
            // Calculate the date for this day
            const targetDate = new Date(currentDate);
            targetDate.setDate(targetDate.getDate() + i);
            targetDate.setHours(0, 0, 0, 0);

            // If store opens tomorrow, return "tomorrow", otherwise return the weekday's name
            const openingDay = i === 1 ? 'tomorrow' : dayOfWeekFullNameAsString(dayToCheck);

            return {
                nextOpeningTime: parse(dayHours.OpenTime, 'HH:mm:ss', targetDate),
                openingDay,
            };
        }
    }

    // No open days found (store is probably closed temporarily)
    return { nextOpeningTime: null, openingDay: null };
};

export const formatToDisplayHours = (date: Date) => format(date, 'haaa');

export const storeIsOpen = (store: DisplayStore, currentDate: Date): boolean => {
    const isStoreClosed = isStoreClosedForDay(store, currentDate.getDay());
    const openingHour = getOpeningTimeToday(store, currentDate);
    const closingHour = getClosingTimeToday(store, currentDate);

    if (!isStoreClosed && openingHour && closingHour) {
        // currentDates datetime must be past opening hour and before closing hour
        return compareAsc(currentDate, openingHour) === 1 && compareAsc(currentDate, closingHour) === -1;
    }

    return false;
};

export const getStoreDisplayTime = (
    store: DisplayStore,
    currentDate: Date,
    format: StoreDisplayTimeFormat = 'default'
): string => {
    const isHeader = format === 'header';

    if (storeIsOpen(store, currentDate)) {
        return `Open until ${formatToDisplayHours(getClosingTimeToday(store, currentDate))}`;
    }

    const { nextOpeningTime, openingDay } = getNextOpeningTime(store, currentDate);

    if (nextOpeningTime && openingDay) {
        const formattedTime = formatToDisplayHours(nextOpeningTime);

        if (openingDay === 'today' || openingDay === 'tomorrow') {
            return `Opens ${openingDay} at ${formattedTime}`;
        }

        if (isHeader) {
            const dayAbbr = dayOfWeekAsString(nextOpeningTime.getDay()).slice(0, 3);
            return `Opens ${dayAbbr} ${formattedTime}`;
        }

        return `Opens ${openingDay} at ${formattedTime}`;
    }

    return isHeader ? 'Temporarily Closed' : 'Store temporarily closed';
};

export const dayOfWeekAsString = (dayIndex: number) =>
    ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'][dayIndex] || '';

export const dayOfWeekFullNameAsString = (dayIndex: number) =>
    ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex] || '';

export const changeTimeFormat = (storeHour: string): string => {
    const hour24 = parseInt(storeHour.slice(0, 2), 10);

    if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) {
        throw new Error(`Invalid hour: "${storeHour}"`);
    }

    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'pm' : 'am';

    return `${hour12}${period}`;
};
